import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { get, post } from '@/lib/api';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Select } from '@/components/ui/primitives';
import { apiMessage } from './LabBenchErrors';

interface RejectionReason {
  id: string;
  code: string;
  label: string;
  isActive: boolean;
}

export function LabRejectDialog({
  itemIds,
  summary,
  onClose,
  onRejected,
}: {
  itemIds: string[];
  summary: string;
  onClose: () => void;
  onRejected: (count: number) => void;
}) {
  const reasons = useQuery({
    queryKey: ['lab-rejection-reasons'],
    queryFn: () => get<RejectionReason[]>('/lab/rejection-reasons'),
    staleTime: 10 * 60_000,
  });
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');

  const reject = useMutation({
    mutationFn: () => post<{ rejected: number }>('/lab/reject', { orderItemIds: itemIds, reasonCode, note: note.trim() || undefined }),
    onSuccess: (result) => onRejected(result.rejected),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Reject ${itemIds.length} specimen${itemIds.length === 1 ? '' : 's'}`}
      description={tr('lab', 'x_the_ward_or_the_ordering_doctor_is_told_and', { summary: summary })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('lab', 'back')}
          </Button>
          <Button variant="destructive" onClick={() => reject.mutate()} loading={reject.isPending} disabled={!reasonCode}>
            {tr('lab', 'reject')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {reject.error && <Alert variant="destructive">{apiMessage(reject.error)}</Alert>}
        {reasons.error && <Alert variant="destructive">{apiMessage(reasons.error)}</Alert>}
        <Field label={tr('lab', 'reason')} required htmlFor="lab-reject-reason">
          <Select id="lab-reject-reason" value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
            <option value="">{tr('lab', 'choose_a_reason')}</option>
            {(reasons.data ?? [])
              .filter((reason) => reason.isActive)
              .map((reason) => (
                <option key={reason.code} value={reason.code}>
                  {reason.label}
                </option>
              ))}
          </Select>
        </Field>
        <Field label={tr('lab', 'note_2')} htmlFor="lab-reject-note" hint={tr('lab', 'optional_what_was_seen')}>
          <Input id="lab-reject-note" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
